import { useState } from 'react';

import { AccessTime, ExitToApp, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { addBranchSend } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { getBranchRootParentIndex, getEffectiveSplitType } from 'domains/campaigns/components/CustomCampaign/utils';
import { SplitTypes } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

import { DisabledTooltip } from '../../../components/DisabledTooltipContent';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';
import { MAX_SENDS_AMOUNT } from '../utils';

const EXIT_FLOW_ACTION = 'exit-flow';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseNextStepRandomSplit = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState<string | null>(null);
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const isEditingPausedCampaign = status === CampaignStatus.PAUSED;

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const maxSendsReached = campaignState.sends.length >= MAX_SENDS_AMOUNT;
    const isTimeDelayDisabled = maxSendsReached || isEditingPausedCampaign;

    const effectiveSplitType = getEffectiveSplitType(activeSend, campaignState.sends);
    const isGroupA = effectiveSplitType === SplitTypes.RANDOM_GROUP_1;
    const branchRootParentIndex = getBranchRootParentIndex(activeSend, campaignState.sends);

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value;
        setSelectedStep(step);

        if (step === CustomCampaignSteps.SET_TIME_DELAY) {
            const branchSplitType = isGroupA ? SplitTypes.RANDOM_GROUP_1 : SplitTypes.RANDOM_GROUP_2;
            dispatchCampaignAction(addBranchSend(activeSendIndex, branchSplitType, activeSend.channel));

            if (isGroupA) {
                const groupBIndex = campaignState.sends.findIndex(
                    (send: any) =>
                        send.splitType === SplitTypes.RANDOM_GROUP_2 && send.parentIndex === branchRootParentIndex
                );
                setActiveSendIndex(groupBIndex !== -1 ? groupBIndex : campaignState.sends.length);
            } else {
                setActiveSendIndex(campaignState.sends.length);
            }

            setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
        }

        if (step === EXIT_FLOW_ACTION) {
            if (isGroupA) {
                const groupBIndex = campaignState.sends.findIndex(
                    (send: any) =>
                        send.splitType === SplitTypes.RANDOM_GROUP_2 && send.parentIndex === branchRootParentIndex
                );
                if (groupBIndex !== -1) {
                    setActiveSendIndex(groupBIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }
            } else {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
            }
        }
    };

    const groupLabel = isGroupA ? 'Group 1' : 'Group 2';
    const exitLabel = isGroupA ? 'proceed to Group 2' : 'done with random split';
    const guidingText = `You've completed an email for ${groupLabel}. Choose to add another time-delayed email to this group, or proceed to the next step.`;

    const steps = [
        {
            disabled: isTimeDelayDisabled,
            disabledMessage: isEditingPausedCampaign ? (
                <DisabledTooltip />
            ) : (
                "You've reached the maximum number of sends."
            ),
            icon: <AccessTime sx={iconStyles} />,
            label: 'time delay',
            value: CustomCampaignSteps.SET_TIME_DELAY,
        },
        {
            disabled: false,
            disabledMessage: '',
            icon: <ExitToApp sx={iconStyles} />,
            label: exitLabel,
            value: EXIT_FLOW_ACTION,
        },
    ];

    return (
        <FormLayout guidingText={guidingText} header="choose next step" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleStepClick} value={selectedStep}>
                {steps.map((step) => (
                    <Tooltip key={step.value} title={step.disabled ? step.disabledMessage : ''}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{
                                ...sharedButtonStyles,
                                ...(step.disabled && disabledButtonStyles),
                            }}
                            value={step.value}
                        />
                    </Tooltip>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseNextStepRandomSplit;
